import React, { useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Calendar, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { BrandLogo } from "../components/BrandLogo";
import { TERMS_VERSION } from "../legal/legalContent";

export const errText = (e, fallback = "Could not complete registration.") => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return (d[0]?.msg || "Please check your details.").replace(/^Value error, /, "");
  return typeof d === "string" ? d : fallback;
};

export const maxDob = () => { const d = new Date(); d.setFullYear(d.getFullYear() - 18); return d.toISOString().slice(0, 10); };

export default function CompleteRegistration() {
  const { user, loading, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [dob, setDob] = useState("");
  const [age, setAge] = useState(false);
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const completed = useRef(false);

  if (loading) return null;
  if (!user) return <Navigate to="/auth" replace />;
  if (!user.registration_incomplete) return <Navigate to={completed.current ? "/onboarding" : "/discover"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!dob) return toast.error("Please enter your date of birth.");
    if (!age) return toast.error("Please confirm you are 18 or older.");
    if (!terms) return toast.error("Please agree to the Terms of Service and Privacy Policy.");
    setBusy(true);
    try {
      await api.post("/auth/complete-registration", { date_of_birth: dob, accepted_terms_version: TERMS_VERSION });
      completed.current = true;
      await refresh();
      toast.success("Welcome to DateBid");
    } catch (err) {
      toast.error(errText(err));
    } finally { setBusy(false); }
  };

  const input = "w-full rounded-xl bg-[#0C0A0A]/60 border border-[#D4AF37]/20 focus:border-[#D4AF37]/60 outline-none pl-11 pr-4 py-3 text-sm text-[#FBF8F3]";
  return (
    <div className="min-h-screen bg-[#0C0A0A] text-[#FBF8F3] flex items-center justify-center px-5" data-testid="complete-registration-page">
      <form onSubmit={submit} className="w-full max-w-md rounded-3xl border border-[#D4AF37]/20 bg-[#141113]/70 p-7 space-y-4">
        <BrandLogo variant="wordmarkSm" className="h-9 w-auto" />
        <h1 className="font-serif text-3xl">Finish creating your account</h1>
        <p className="text-sm text-[#9E978E]">DateBid is for adults 18+. Add your date of birth and accept our Terms to continue. Your full birthday is never shown publicly.</p>
        <div className="relative">
          <Calendar size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9E978E]" />
          <input data-testid="complete-dob-input" type="date" required max={maxDob()} value={dob} onChange={(e) => setDob(e.target.value)} className={input} />
        </div>
        <label className="flex items-start gap-3 text-sm text-[#D8D2C9] cursor-pointer">
          <input data-testid="complete-age-checkbox" type="checkbox" checked={age} onChange={(e) => setAge(e.target.checked)} className="mt-1 accent-[#D4AF37]" />
          <span>I confirm I am at least 18 years old and agree to meet responsibly and in public.</span>
        </label>
        <label className="flex items-start gap-3 text-sm text-[#D8D2C9] cursor-pointer">
          <input data-testid="complete-terms-checkbox" type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="mt-1 accent-[#D4AF37]" />
          <span>I agree to the <a href="/legal/terms" target="_blank" rel="noreferrer" className="text-[#D4AF37] underline">Terms of Service</a> and acknowledge the <a href="/legal/privacy" target="_blank" rel="noreferrer" className="text-[#D4AF37] underline">Privacy Policy</a>.</span>
        </label>
        <button data-testid="complete-submit-btn" type="submit" disabled={busy} className="db-offer-btn w-full rounded-full py-3.5 text-sm font-semibold flex items-center justify-center gap-2">
          {busy ? "Please wait…" : "Continue"} <ArrowRight size={16} />
        </button>
        <button type="button" data-testid="complete-logout-btn" onClick={() => logout().then(() => nav("/"))} className="w-full text-center text-xs text-[#9E978E] hover:text-[#D4AF37] transition-colors">Sign out</button>
      </form>
    </div>
  );
}
